import React, { useEffect, useState } from 'react';

import { Input, Page } from '@components';
import { ShadowContainer } from '@styles/components/wrapper';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Form, Row } from 'antd';
import { Title } from '@styles/components/text';
import { columns, filterTypes } from './constants';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { Table } from '@components/table';
import { FormItem, Option, Select } from '@styles/components/form';
import { useHistory } from 'react-router-dom';
import { toQueryParameters } from 'src/extensions/object';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
    fetchCommercialBonusesSales,
    commercialBonusSaleSelector,
    exportCommercialBonusesSales,
} from '@redux/slices/reports';
import { ExportButton } from '@styles/components/button';
import { getFilterOptions } from '@utils/filter';
import { CommercialBonusSaleResponse } from '@models/report';
import { translate } from '@components/i18n';
import { fetchMoedasSimbolos } from '@redux/slices/order';

export const CommercialBonusesSalesReport: React.FC = () => {
    const [queryState, setQueryState] = useState<string>('');
    const commercialBonusSaleState = useSelector(commercialBonusSaleSelector);
    const dispatch = useDispatch();
    const history = useHistory();
    const [form] = Form.useForm();
    const [optionsComparisonFilter, setOptionsComparisonFilter] =
        useState(null);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFiltersChange = (changedValues: any, values: any): void => {
        if (changedValues.filterBy) {
            setOptionsComparisonFilter(
                getFilterOptions(filterTypes, changedValues.filterBy),
            );
        }
        dispatch(
            fetchCommercialBonusesSales({
                ...commercialBonusSaleState.request,
                ...values,
                filterValue: queryState,
            }),
        );
    };

    const onSearch = (value: string): void => {
        setQueryState(value);
    };

    const onExport = (): void => {
        dispatch(
            exportCommercialBonusesSales({
                ...commercialBonusSaleState.request,
                filterValue: queryState,
            }),
        );
    };

    useEffect(() => {
        history.replace(
            '/reports/commercial-bonuses-sales?' +
                toQueryParameters(commercialBonusSaleState.request),
        );
    }, [commercialBonusSaleState.request]);

    useEffect(() => {
        dispatch(fetchMoedasSimbolos())
        dispatch(fetchCommercialBonusesSales(commercialBonusSaleState.request));
        setOptionsComparisonFilter(
            getFilterOptions(filterTypes, 'pedido_bonificacao'),
        );
    }, []);

    const loadData = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pagination: any,
        filters: Record<string, FilterValue>,
        sorter: SorterResult<unknown>,
    ): void => {
        dispatch(
            fetchCommercialBonusesSales({
                ...pagination,
                field: sorter.field,
                order: sorter.order,
            }),
        );
    };

    const search = (): void => {
        dispatch(
            fetchCommercialBonusesSales({
                ...commercialBonusSaleState.request,
                filterValue: queryState,
            }),
        );
    };

    useEffect(() => {
        const filters = Object.fromEntries(
            new URLSearchParams(location.search),
        );

        setTimeout(() => {
            form.setFieldsValue({
                ...filters,
                filterBy: filters.filterBy || undefined,
                comparisonType: filters.comparisonType || undefined,
            });
        }, 2000);
    }, []);

    const onSelect = (rowKey: CommercialBonusSaleResponse): void => {
        history.push(`/approval-order/${rowKey.saleOrder}?readOnly=true`);
    };

    return (
        <Page
            title={translate('pages.comercialBonus')}
            greyBackground
            withPadding
        >
            <Row>
                <Col span={1} />
                <Col span={22}>
                    <Row>
                        <Title marginTop={20} level={3}>
                            {translate('titlePages.comercialBonus')}
                        </Title>
                    </Row>
                    <ShadowContainer>
                        <Row justify="center">
                            <Col span={23}>
                                <Table
                                    customKey={`${commercialBonusSaleState.request.current}-page`}
                                    columns={columns.filter(c => c)}
                                    form={form}
                                    pointer
                                    dataSource={
                                        commercialBonusSaleState.commercialBonusesSales
                                    }
                                    pagination={
                                        commercialBonusSaleState.request
                                    }
                                    isLoading={
                                        commercialBonusSaleState.isFetching
                                    }
                                    onRowClick={onSelect}
                                    onChange={loadData}
                                    onFilterChange={onFiltersChange}
                                    filters={[
                                        <FormItem
                                            label={translate(
                                                'forms.labels.searchFor',
                                            )}
                                            name={'filterBy'}
                                        >
                                            <Select
                                                placeholder={translate(
                                                    'general.all',
                                                )}
                                                dropdownMatchSelectWidth={false}
                                                allowClear
                                            >
                                                {filterTypes
                                                .filter(c => !!c)
                                                .map((f) => {
                                                    return (
                                                        <Option
                                                            key={f.name}
                                                            value={f.name}
                                                        >
                                                            {f.description}
                                                        </Option>
                                                    );
                                                })}
                                            </Select>
                                        </FormItem>,
                                        <FormItem
                                            label={translate(
                                                'forms.labels.searchType',
                                            )}
                                            name={'comparisonType'}
                                        >
                                            <Select
                                                placeholder={translate(
                                                    'general.all',
                                                )}
                                                dropdownMatchSelectWidth={false}
                                                allowClear
                                            >
                                                {optionsComparisonFilter &&
                                                    optionsComparisonFilter.map(
                                                        (f) => {
                                                            return (
                                                                <Option
                                                                    key={f.name}
                                                                    value={
                                                                        f.name
                                                                    }
                                                                >
                                                                    {
                                                                        f.description
                                                                    }
                                                                </Option>
                                                            );
                                                        },
                                                    )}
                                            </Select>
                                        </FormItem>,
                                        <Form.Item wrapperCol={{ span: 24 }}>
                                            <Input
                                                placeholder={translate(
                                                    'forms.placeholders.search',
                                                )}
                                                allowClear
                                                suffix={
                                                    <div
                                                        onClick={search}
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        <SearchOutlined />
                                                    </div>
                                                }
                                                onKeyUp={(e) => {
                                                    if (
                                                        e.code === `Enter` ||
                                                        e.keyCode === 13
                                                    ) {
                                                        search();
                                                    }
                                                }}
                                                onChange={(e) =>
                                                    onSearch(e.target.value)
                                                }
                                            />
                                        </Form.Item>,
                                    ]}
                                    actions={[
                                        <ExportButton
                                            onClick={() => {
                                                onExport();
                                            }}
                                        >
                                            {translate('general.export')}{' '}
                                            <DownloadOutlined />
                                        </ExportButton>,
                                    ]}
                                />
                            </Col>
                        </Row>
                    </ShadowContainer>
                </Col>
                <Col span={1} />
            </Row>
        </Page>
    );
};
